import { CollapseSection } from '@/components/ats';
import { Flex } from '@chakra-ui/react';
import {
  capitalizeFirstLetter,
  checkForInvalidVariables,
} from '@/utils/commonHelpers';
import { ChooseTemplate } from '../../email/ChooseTemplate';
import * as Yup from 'yup';
import { getInvalidVariableAlertConfig } from '@/utils/getInvalidVariableAlertConfig';

const labelStyles = {
  fontSize: '12px',
  fontWeight: 'semiBold',
  minWidth: '50px',
};
const containerStyles = {
  paddingLeft: '24px',
  paddingRight: '24px',
  margin: '0px',
  borderColor: 'border-color',
};

const collapseSectionProps = {
  headerConfig: {
    containerStyles: () => ({
      // minW: '600px',
      backgroundColor: 'ats-tertiary',
      h: '44px',
      alignItems: 'center',
      pl: 'xxl',
      pr: 'xxl',
    }),
    textStyles: {
      color: 'ats-primary',
      textTransform: 'uppercase',
      fontSize: 'sm',
    },
    iconStyles: {
      width: '24px',
      height: '24px',
      color: 'ats-primary',
    },
  },
  collapseStyles: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
    margin: '24px',
  },
};

/** Custom-variable groups, keyed by section; each holds the variable names. */
type VariableList = Record<string, string[]>;

interface FormWrapperConfigArgs {
  /** Prefix for the generated field ids (e.g. `interviewer`, `candidate`). */
  preText: string;
  title?: string;
  variables?: VariableList;
  template?: { body?: string };
  selectOnChange?: (_template?: {
    id?: string | number;
    name?: string;
  }) => void;
  /** Template category passed through to the picker's filter. */
  type?: string | string[];
  setDefault?: boolean;
}

export const getFormWrapperConfig = ({
  preText,
  title,
  variables,
  template,
  selectOnChange = () => {},
  type,
  setDefault = false,
}: FormWrapperConfigArgs) => {
  const { body: bodyVal } = template || {};
  const variablesListArr = Object.entries(variables || {}).flatMap(
    ([, values = []]) => values
  );

  return {
    id: preText,
    type: 'wrapper',
    fields: [
      {
        type: 'custom',
        Field: ChooseTemplate,
        props: {
          selectOnChange,
          setDefault,
          channel: 'calendar_invite',
          category: type,
        },
      },
      {
        id: `${preText}EmailWrapper`,
        type: 'wrapper',
        fields: [
          {
            name: `${preText}EmailEditor`,
            enableCustomVariables: true,
            type: 'editor',
            isRequired: false,
            containerStyles: { width: '100%', ...containerStyles },
            labelStyles: { ...labelStyles, marginBottom: '0px' },
            editorContainerStyles: { border: 'none' },
            editorWraperStyles: { padding: '0px', paddingTop: '10px' },
            editorStyles: { padding: '0px', paddingTop: '10px' },
            toolbarWrapperStyles: { marginLeft: '-10px' },
            customVariableList: variables,
            content: bodyVal,
            replaceContent: true,
            errorMessage: `${title} is required`,
            optionalValidation: true,
            validation: Yup.string().test(
              'check-for-invalid-variables',
              '',
              function (value?: string) {
                const invalidVariablesFound = checkForInvalidVariables({
                  variablesListArr: Object.entries(variables || {}).flatMap(
                    ([, values = []]) => values
                  ),
                  content: value,
                });

                if (invalidVariablesFound) return false;
                else return true;
              }
            ),
          },
        ],
        Wrapper: Flex,
        wrapperProps: {
          flexDir: 'column',
          border: '1px solid',
          borderColor: 'border-color',
          marginTop: '12px',
          borderRadius: 'md',
        },
      },
      getInvalidVariableAlertConfig({
        variablesListArr: variablesListArr,
        key: `${preText}EmailEditor`,
        isBodySubject: false,
      }),
    ],
    Wrapper: CollapseSection,
    wrapperProps: {
      ...collapseSectionProps,
      title: title
        ? capitalizeFirstLetter(title)
        : `${capitalizeFirstLetter(preText)} Email Template`,
      switchFieldName: `show${capitalizeFirstLetter(preText)}Template`,
      defaultIsOpen: true,
      isHeaderClickable: true,
      styles: { height: '100%', overflow: 'hidden' },
    },
  };
};
